import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Engineering,
  PrecisionManufacturing,
  Queue,
  LocalShipping,
  AttachMoney,
  Add,
  CheckCircle,
  Build,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { BRAND } from '../../config/brand';
import {
  useGetPipelineQuery,
  useGetProductionStatsQuery,
} from '../../api/productionFlowApi';

const STAGES = [
  'order', 'design', 'queue', 'cutting', 'cnc', 'assembly',
  'finishing', 'qc', 'storage', 'dispatch', 'delivered',
];

const stageLabels: Record<string, string> = {
  order: 'Order', design: 'Design', queue: 'Queue', cutting: 'Cutting',
  cnc: 'CNC', assembly: 'Assembly', finishing: 'Finishing', qc: 'QC',
  storage: 'Storage', dispatch: 'Dispatch', delivered: 'Delivered',
};

export const ProductionOperationsHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';

  const { data: pipeline, isLoading: pipelineLoading } = useGetPipelineQuery();
  const { data: stats } = useGetProductionStatsQuery();

  const statCards = [
    { label: 'Active Orders', value: stats?.activeOrders ?? 0, icon: <Engineering />, color: BRAND.colors.navy },
    { label: 'Machines Running', value: stats?.machinesRunning ?? 0, icon: <PrecisionManufacturing />, color: BRAND.colors.navyLight },
    { label: 'Queue Depth', value: stats?.queueDepth ?? 0, icon: <Queue />, color: BRAND.colors.navyDark },
    { label: 'Deliveries Today', value: stats?.deliveriesToday ?? 0, icon: <LocalShipping />, color: '#2e7d32' },
    { label: 'Revenue', value: `$${(stats?.revenue ?? 0).toLocaleString()}`, icon: <AttachMoney />, color: '#ed6c02' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: BRAND.colors.navy, fontWeight: 700 }}>
          Production Operations Hub
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Build />}
            onClick={() => navigate(`${basePath}/production/machines`)}
            sx={{ borderColor: BRAND.colors.navy, color: BRAND.colors.navy }}
          >
            Machine Floor
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`${basePath}/orders`)}
            sx={{ backgroundColor: BRAND.colors.navy }}
          >
            Create from Order
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={2.4} key={card.label}>
            <Card sx={{ borderTop: `4px solid ${card.color}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {pipelineLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: BRAND.colors.navy }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2 }}>
          {STAGES.map((stage) => {
            const items = (pipeline?.[stage] as Array<Record<string, unknown>>) || [];
            return (
              <Paper
                key={stage}
                sx={{
                  minWidth: 200,
                  maxWidth: 220,
                  p: 1.5,
                  backgroundColor: `rgba(27, 77, 137, 0.04)`,
                  borderTop: `3px solid ${BRAND.colors.navy}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: BRAND.colors.navy, fontWeight: 700, mb: 1 }}>
                  {stageLabels[stage]} ({items.length})
                </Typography>
                {items.map((wo) => (
                  <Paper
                    key={wo.id as string}
                    sx={{ p: 1, mb: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => navigate(`${basePath}/production/work-orders/${wo.id}`)}
                    elevation={2}
                  >
                    <Typography variant="caption" fontWeight={600}>
                      {wo.workOrderNumber as string}
                    </Typography>
                    <Chip size="small" label={wo.productType as string} sx={{ ml: 0.5, height: 18 }} />
                    <Typography variant="caption" display="block" color="text.secondary">
                      P{wo.priority as number}
                    </Typography>
                  </Paper>
                ))}
              </Paper>
            );
          })}
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<CheckCircle />}
          onClick={() => navigate(`${basePath}/production/queue`)}
        >
          Production Queue
        </Button>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/warehouse`)}>
          Warehouse
        </Button>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/deliveries`)}>
          Deliveries
        </Button>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/financials`)}>
          Financials
        </Button>
      </Box>
    </Box>
  );
};

export default ProductionOperationsHub;
